/**
 * Timeline entry components — vertical step-dot design with collapsible detail cards.
 */

import { useState } from "react";
import { Badge } from "@/ui/primitives/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  Brain,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronDown,
  MessageSquare,
  Activity,
  Wrench,
  Play,
  AlertTriangle,
  Info,
  Sparkles,
  Cog,
  Users,
  GitMerge,
  Layers,
  Rocket,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { parseAgentText } from "../shared";
import type { TimelineEntry, GroupedTimelineItem } from "./timeline-types";
import { fmtTime } from "./timeline-helpers";
import { AgentTextSegment } from "./AgentTextSegment";
import { AgentChip } from "./AgentChip";
import { parseActivityMessage } from "./activity-parsing";
import { formatToolName, getToolColor, getToolIcon } from "./tool-display";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

// ---------------------------------------------------------------------------
// Step wrapper — provides the dot + vertical line
// ---------------------------------------------------------------------------

function StepRow({
  icon,
  iconColor,
  isActive,
  isLast,
  children,
}: {
  icon: React.ReactNode;
  iconColor?: string;
  isActive?: boolean;
  isLast?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex gap-3">
      {/* Vertical line */}
      {!isLast && (
        <div className="absolute bottom-0 left-[11px] top-[28px] w-px bg-border" />
      )}
      {/* Dot / Icon — `relative` (not z-10) keeps the dot painted over the
          absolute vertical line via DOM order while staying contained inside
          the scroll viewport's stacking context. */}
      <div
        className={cn(
          "relative flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-full border",
          isActive
            ? "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40"
            : "border-border bg-background",
          iconColor,
        )}
      >
        {icon}
      </div>
      {/* Content */}
      <div className="min-w-0 flex-1 pb-4">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity Entry — dispatches to the right v2 row component or falls back to
// the v1 keyword-styled row for older agent strings.
// ---------------------------------------------------------------------------

export function TimelineActivityEntry({
  entry,
  isLast,
}: {
  entry: Extract<TimelineEntry, { type: "activity" }>;
  isLast?: boolean;
}) {
  const parsed = parseActivityMessage(entry.message);

  if (parsed.kind === "callout") {
    return (
      <TimelineCalloutEntry
        agent={parsed.agent}
        body={parsed.body}
        timestamp={entry.timestamp}
        isLast={isLast}
      />
    );
  }

  if (parsed.kind === "narrative") {
    return (
      <TimelineNarrativeEntry
        agent={parsed.agent}
        body={parsed.body}
        counts={parsed.counts}
        timestamp={entry.timestamp}
        isLast={isLast}
      />
    );
  }

  if (parsed.kind === "session") {
    return (
      <TimelineSessionRow
        agent={parsed.agent}
        body={parsed.body}
        tone={parsed.tone}
        timestamp={entry.timestamp}
        isLast={isLast}
      />
    );
  }

  // tool_call / tool_result are normally folded into a `tool_pair` by
  // groupEntries; this fallback only triggers if pairing was skipped (e.g.
  // a stray result without a matching call).
  if (parsed.kind === "tool_call" || parsed.kind === "tool_result") {
    const isPending = parsed.kind === "tool_call";
    return (
      <TimelineToolCallEntry
        pair={{
          kind: "tool_pair",
          agent: parsed.agent,
          toolName: parsed.toolName,
          callTimestamp: entry.timestamp,
          resultTimestamp: isPending ? undefined : entry.timestamp,
          status: isPending ? "pending" : "completed",
          key: entry.key,
        }}
        isLast={isLast}
      />
    );
  }

  // Plain — keep the v1 keyword-based styling so old strings (coordinator /
  // worker / dispatching / ...) still get nice icons.
  const styleSource = parsed.agent ? parsed.body : entry.message;
  const style = getActivityStyle(styleSource);
  const displayText = parsed.agent ? parsed.body : entry.message;

  return (
    <StepRow
      icon={style.icon}
      iconColor={style.iconColor}
      isActive={style.isActive}
      isLast={isLast}
    >
      <div className="flex items-start gap-2 pr-3 pt-0.5">
        {parsed.agent && <AgentChip agent={parsed.agent} />}
        <p
          className={cn(
            "min-w-0 flex-1 break-words text-xs leading-relaxed",
            style.textClass,
          )}
        >
          {displayText}
        </p>
        <span className="shrink-0 whitespace-nowrap pt-0.5 font-mono text-[10px] tabular-nums text-muted-foreground/50">
          {fmtTime(entry.timestamp)}
        </span>
      </div>
    </StepRow>
  );
}

function getActivityStyle(message: string): {
  icon: React.ReactNode;
  iconColor?: string;
  isActive?: boolean;
  textClass: string;
} {
  const msg = message.toLowerCase();

  // Coordinator iterations
  if (msg.includes("coordinator") && msg.includes("iteration")) {
    return {
      icon: <Brain className="h-3 w-3 text-primary" />,
      textClass: "text-foreground font-medium",
    };
  }
  if (
    msg.includes("coordinator") &&
    (msg.includes("surveying") || msg.includes("planning"))
  ) {
    return {
      icon: <Brain className="h-3 w-3 text-primary" />,
      textClass: "text-foreground font-medium",
    };
  }

  // Worker dispatching
  if (msg.includes("dispatching") && msg.includes("worker")) {
    return {
      icon: <Rocket className="h-3 w-3 text-blue-500" />,
      isActive: true,
      textClass: "text-blue-700 dark:text-blue-300 font-medium",
    };
  }

  // Worker started
  if (msg.startsWith("worker [") && !msg.includes("complete")) {
    return {
      icon: <Cog className="h-3 w-3 animate-spin text-blue-400" />,
      isActive: true,
      textClass: "text-foreground/70",
    };
  }

  // Worker completed
  if (msg.includes("worker") && msg.includes("complete")) {
    return {
      icon: <CheckCircle2 className="h-3 w-3 text-emerald-500" />,
      textClass: "text-emerald-700 dark:text-emerald-400",
    };
  }

  // All workers complete
  if (msg.includes("all workers complete")) {
    return {
      icon: <Users className="h-3 w-3 text-emerald-600" />,
      textClass: "text-emerald-700 dark:text-emerald-400 font-medium",
    };
  }

  // Dedup / batch / sweep
  if (
    msg.includes("dedup") ||
    (msg.includes("reviewing") && msg.includes("entries"))
  ) {
    return {
      icon: <GitMerge className="h-3 w-3 text-violet-500" />,
      textClass: "text-violet-700 dark:text-violet-400",
    };
  }
  if (msg.includes("batch")) {
    return {
      icon: <Layers className="h-3 w-3 text-violet-400" />,
      textClass: "text-foreground/70",
    };
  }
  if (msg.includes("sweep")) {
    return {
      icon: <RotateCcw className="h-3 w-3 text-violet-400" />,
      textClass: "text-foreground/70",
    };
  }

  // Extraction complete
  if (msg.includes("extraction complete")) {
    return {
      icon: <CheckCircle2 className="h-3 w-3 text-emerald-500" />,
      textClass: "text-emerald-700 dark:text-emerald-400 font-medium",
    };
  }

  // Recovery tasks
  if (msg.includes("recovery")) {
    return {
      icon: <AlertTriangle className="h-3 w-3 text-amber-500" />,
      textClass: "text-amber-700 dark:text-amber-400",
    };
  }

  // Starting agent
  if (msg.includes("starting")) {
    return {
      icon: <Play className="h-3 w-3 text-primary" />,
      textClass: "text-foreground font-medium",
    };
  }

  // Complete
  if (
    msg.includes("complete") &&
    (msg.includes("signal") || msg.includes("entities"))
  ) {
    return {
      icon: <CheckCircle2 className="h-3 w-3 text-emerald-600" />,
      textClass: "text-emerald-700 dark:text-emerald-400 font-medium",
    };
  }

  // Default
  return {
    icon: <Activity className="h-3 w-3 text-muted-foreground/60" />,
    textClass: "text-foreground/70",
  };
}

// ---------------------------------------------------------------------------
// Lifecycle Entry — started/completed/failed with badge
// ---------------------------------------------------------------------------

export function TimelineLifecycleEntry({
  entry,
  isLast,
}: {
  entry: Extract<TimelineEntry, { type: "lifecycle" }>;
  isLast?: boolean;
}) {
  const iconMap = {
    started: <Play className="h-3 w-3 text-blue-500" />,
    completed: <CheckCircle2 className="h-3 w-3 text-emerald-500" />,
    failed: <XCircle className="h-3 w-3 text-red-500" />,
  };

  return (
    <StepRow
      icon={iconMap[entry.event]}
      isActive={entry.event === "started"}
      isLast={isLast}
    >
      <div className="flex items-center gap-2 pr-3 pt-0.5">
        <span className="text-xs font-medium text-foreground">
          {entry.label}
        </span>
        {entry.event === "started" && (
          <Badge color="blue" variant="soft" size="sm">
            Started
          </Badge>
        )}
        {entry.event === "completed" && (
          <Badge color="emerald" variant="soft" size="sm">
            Completed{entry.entityCount != null && ` (${entry.entityCount})`}
          </Badge>
        )}
        {entry.event === "failed" && (
          <Badge color="red" variant="soft" size="sm">
            Failed
          </Badge>
        )}
        <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground/50">
          {fmtTime(entry.timestamp)}
        </span>
      </div>
      {entry.event === "failed" && entry.error && (
        <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">
          {entry.error}
        </p>
      )}
    </StepRow>
  );
}

// ---------------------------------------------------------------------------
// Message Group — collapsible card showing coordinator/worker messages
// ---------------------------------------------------------------------------

export function TimelineMessageGroup({
  group,
  isLast,
}: {
  group: Extract<GroupedTimelineItem, { kind: "message_group" }>;
  isLast?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(group.hasStreaming);
  const isCoordinator = group.source === "main";

  // Count tool calls in the group
  const toolCalls = group.entries.filter((e) => e.role === "tool").length;
  const assistantEntries = group.entries.filter((e) => e.role === "assistant");

  // Extract a summary from the first assistant message
  const firstText = getFirstAssistantText(assistantEntries);
  const summary = firstText
    ? firstText.length > 120
      ? firstText.slice(0, 118) + "..."
      : firstText
    : null;

  return (
    <StepRow
      icon={
        group.hasStreaming ? (
          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
        ) : (
          <Brain className="h-3 w-3 text-primary" />
        )
      }
      isActive={group.hasStreaming}
      isLast={isLast}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="group w-full text-left">
          <div className="flex items-center gap-2 pr-3 pt-0.5">
            <span className="text-xs font-medium text-foreground">
              {isCoordinator ? "Coordinator" : group.sourceLabel}
            </span>
            {toolCalls > 0 && (
              <Badge variant="outline" size="sm" className="gap-0.5">
                <Wrench className="h-2.5 w-2.5" />
                {toolCalls}
              </Badge>
            )}
            {group.hasStreaming && (
              <span className="text-[10px] text-blue-600 dark:text-blue-400">
                Streaming...
              </span>
            )}
            <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground/50">
              {fmtTime(group.firstTimestamp)}
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground/40 transition-transform duration-150",
                isOpen && "rotate-180",
              )}
            />
          </div>
          {!isOpen && summary && (
            <p className="mt-1 line-clamp-1 text-[11px] text-muted-foreground/60">
              {summary}
            </p>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 overflow-hidden rounded-lg border border-border bg-muted/10">
            <div className="divide-y divide-border/50">
              {group.entries.map((entry) => (
                <InlineAgentMessage key={entry.key} entry={entry} />
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </StepRow>
  );
}

// ---------------------------------------------------------------------------
// Inline Agent Message (inside the collapsible card)
// ---------------------------------------------------------------------------

function InlineAgentMessage({
  entry,
}: {
  entry: Extract<TimelineEntry, { type: "agent_message" }>;
}) {
  const msg = entry.message.message;
  if (!msg) return null;

  // Narrow on `msg.role` (not `entry.role`) so TypeScript can discriminate
  // `msg.content`'s shape; the two are always in sync since `entry.role` is
  // copied from this same message when the timeline entry is built.
  if (msg.role === "user") {
    return (
      <Collapsible>
        <div className="px-3 py-2">
          <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground">
            <MessageSquare className="h-3 w-3" />
            <span className="font-medium">Prompt</span>
            <ChevronDown className="h-3 w-3" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted/30 p-2 text-[10px] text-muted-foreground">
              {typeof msg.content === "string"
                ? msg.content
                : JSON.stringify(msg.content, null, 2)}
            </pre>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  }

  if (msg.role === "assistant") {
    const textParts: string[] = [];
    const toolCalls: Array<{ name: string; args?: unknown }> = [];

    if (typeof msg.content === "string") {
      textParts.push(msg.content);
    } else if (Array.isArray(msg.content)) {
      for (const part of msg.content) {
        if (part.type === "text" && part.text) textParts.push(part.text);
        if (part.type === "tool-call") {
          toolCalls.push({
            name: part.toolName ?? "tool",
            args: part.args,
          });
        }
      }
    }

    const fullText = textParts.join("\n");
    const hasText = fullText.trim().length > 0;
    const hasTools = toolCalls.length > 0;

    if (!hasText && !hasTools) return null;

    const segments = hasText ? parseAgentText(fullText) : [];

    return (
      <div className="flex flex-col gap-1.5 px-3 py-2">
        {segments.map((seg, i) => (
          <AgentTextSegment key={i} segment={seg} />
        ))}
        {hasTools && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            {toolCalls.map((tc, i) => (
              <Collapsible key={i}>
                <CollapsibleTrigger className="inline-flex">
                  <Badge
                    color={getToolColor(tc.name)}
                    variant="soft"
                    size="sm"
                    className="cursor-pointer gap-1 transition-opacity hover:opacity-80"
                  >
                    {getToolIcon(tc.name)}
                    {formatToolName(tc.name)}
                    <ChevronDown className="h-2.5 w-2.5" />
                  </Badge>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted/20 p-2 font-mono text-[10px] text-muted-foreground">
                    {JSON.stringify(tc.args, null, 2)}
                  </pre>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
        {entry.streaming && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Streaming...
          </span>
        )}
      </div>
    );
  }

  if (msg.role === "tool") {
    const results: unknown[] = Array.isArray(msg.content)
      ? msg.content
      : [msg.content];
    const hasError = results.some(
      (r) => isRecord(r) && (Boolean(r.error) || Boolean(r.isError)),
    );
    return (
      <Collapsible>
        <CollapsibleTrigger className="w-full">
          <div className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[11px] transition-colors hover:bg-muted/20">
            {hasError ? (
              <XCircle className="h-3 w-3 shrink-0 text-red-500" />
            ) : (
              <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />
            )}
            <span className="flex-1 truncate text-left text-muted-foreground">
              Result
            </span>
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground/30" />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="mx-3 mb-2 max-h-32 overflow-auto rounded bg-muted/20 p-2 font-mono text-[10px] text-muted-foreground">
            {JSON.stringify(
              results.length === 1 ? results[0] : results,
              null,
              2,
            )}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Tool Pair — paired call/result rendered as a single animated pill
// ---------------------------------------------------------------------------

export function TimelineToolCallEntry({
  pair,
  isLast,
}: {
  pair: Extract<GroupedTimelineItem, { kind: "tool_pair" }>;
  isLast?: boolean;
}) {
  const isPending = pair.status === "pending";
  const toolColor = getToolColor(pair.toolName);

  return (
    <StepRow
      icon={
        isPending ? (
          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
        ) : (
          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
        )
      }
      isActive={isPending}
      isLast={isLast}
    >
      <div className="flex min-w-0 items-center gap-2 pr-3 pt-0.5">
        <AgentChip agent={pair.agent} />
        <Badge
          color={toolColor}
          variant="soft"
          size="sm"
          className={cn(
            "shrink-0 gap-1 font-medium tabular-nums",
            isPending &&
              "animate-pulse ring-1 ring-blue-300/50 dark:ring-blue-700/40",
          )}
        >
          {getToolIcon(pair.toolName)}
          {formatToolName(pair.toolName)}
        </Badge>
        {isPending ? (
          <span className="truncate text-[10px] text-blue-600 dark:text-blue-400">
            Running...
          </span>
        ) : (
          <span className="truncate text-[10px] text-muted-foreground/70">
            Returned
          </span>
        )}
        <span className="ml-auto shrink-0 whitespace-nowrap font-mono text-[10px] tabular-nums text-muted-foreground/50">
          {fmtTime(pair.callTimestamp)}
        </span>
      </div>
    </StepRow>
  );
}

// ---------------------------------------------------------------------------
// Callout — amber alert card with the agent chip and body text
// ---------------------------------------------------------------------------

export function TimelineCalloutEntry({
  agent,
  body,
  timestamp,
  isLast,
}: {
  agent: string;
  body: string;
  timestamp: number;
  isLast?: boolean;
}) {
  return (
    <StepRow
      icon={<AlertTriangle className="h-3 w-3 text-amber-500" />}
      isLast={isLast}
    >
      <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30">
        <div className="mb-1 flex items-center gap-2">
          <AgentChip agent={agent} />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            Callout
          </span>
          <span className="ml-auto shrink-0 whitespace-nowrap font-mono text-[10px] tabular-nums text-muted-foreground/50">
            {fmtTime(timestamp)}
          </span>
        </div>
        <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-amber-900 dark:text-amber-200">
          {body}
        </p>
      </div>
    </StepRow>
  );
}

// ---------------------------------------------------------------------------
// Narrative — soft card for the agent's longer per-tool/per-doc summaries
// ---------------------------------------------------------------------------

export function TimelineNarrativeEntry({
  agent,
  body,
  counts,
  timestamp,
  isLast,
}: {
  agent: string;
  body: string;
  counts?: {
    inserted: number;
    updated: number;
    merged: number;
    skipped: number;
  };
  timestamp: number;
  isLast?: boolean;
}) {
  return (
    <StepRow
      icon={<Sparkles className="h-3 w-3 text-primary" />}
      isLast={isLast}
    >
      <div className="rounded-md border border-border bg-card px-3 py-2 shadow-sm">
        <div className="mb-1.5 flex items-center gap-2">
          <AgentChip agent={agent} />
          <span className="ml-auto shrink-0 whitespace-nowrap font-mono text-[10px] tabular-nums text-muted-foreground/60">
            {fmtTime(timestamp)}
          </span>
        </div>
        <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground">
          {body}
        </p>
        {counts && hasAnyCount(counts) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {counts.inserted > 0 && (
              <Badge
                color="emerald"
                variant="soft"
                size="sm"
                className="gap-0.5"
              >
                +{counts.inserted} inserted
              </Badge>
            )}
            {counts.updated > 0 && (
              <Badge color="blue" variant="soft" size="sm" className="gap-0.5">
                {counts.updated} updated
              </Badge>
            )}
            {counts.merged > 0 && (
              <Badge
                color="violet"
                variant="soft"
                size="sm"
                className="gap-0.5"
              >
                {counts.merged} merged
              </Badge>
            )}
            {counts.skipped > 0 && (
              <Badge color="gray" variant="soft" size="sm" className="gap-0.5">
                {counts.skipped} skipped
              </Badge>
            )}
          </div>
        )}
      </div>
    </StepRow>
  );
}

function hasAnyCount(c: {
  inserted: number;
  updated: number;
  merged: number;
  skipped: number;
}): boolean {
  return c.inserted > 0 || c.updated > 0 || c.merged > 0 || c.skipped > 0;
}

// ---------------------------------------------------------------------------
// Session — small info/error row for session lifecycle log lines
// ---------------------------------------------------------------------------

export function TimelineSessionRow({
  agent,
  body,
  tone,
  timestamp,
  isLast,
}: {
  agent: string;
  body: string;
  tone: "info" | "error";
  timestamp: number;
  isLast?: boolean;
}) {
  const isError = tone === "error";
  return (
    <StepRow
      icon={
        isError ? (
          <XCircle className="h-3 w-3 text-red-500" />
        ) : (
          <Info className="h-3 w-3 text-muted-foreground/60" />
        )
      }
      isLast={isLast}
    >
      <div className="flex items-center gap-2 pr-3 pt-0.5">
        <AgentChip agent={agent} />
        <span
          className={cn(
            "min-w-0 flex-1 break-words text-xs leading-relaxed",
            isError
              ? "text-red-700 dark:text-red-400"
              : "text-muted-foreground",
          )}
        >
          {body}
        </span>
        <span className="shrink-0 whitespace-nowrap font-mono text-[10px] tabular-nums text-muted-foreground/50">
          {fmtTime(timestamp)}
        </span>
      </div>
    </StepRow>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFirstAssistantText(
  entries: Extract<TimelineEntry, { type: "agent_message" }>[],
): string | null {
  for (const entry of entries) {
    const msg = entry.message.message;
    if (!msg || msg.role !== "assistant") continue;
    if (typeof msg.content === "string") return msg.content.trim();
    if (Array.isArray(msg.content)) {
      for (const part of msg.content) {
        if (part.type === "text" && part.text?.trim()) return part.text.trim();
      }
    }
  }
  return null;
}
